import { v4 as uuid } from 'uuid';
import UmlPackageShape from '../../Types/UmlPackageShape';
import UmlSeqParticipantShape from '../../Types/UmlSeqParticipantShape';
import UmlSeqLifelineShape from '../../Types/UmlSeqLifelineShape';
import UmlSeqMessageShape from '../../Types/UmlSeqMessageShape';
import UmlSeqActivationShape from '../../Types/UmlSeqActivationShape';
import UmlCaption from '../../Types/UmlCaption';
import umlCanvasEngine from '../UmlCanvasEngine';
import { ACTOR_NAMESPACE } from './CsSeqShapeFactory';

const PARTICIPANT_WIDTH = 160;
const PARTICIPANT_HEIGHT = 44;
const PARTICIPANT_GAP = 24;
const ACTOR_WIDTH = 90; // narrower than a normal participant box — just a stick figure + name, see UmlSeqParticipantWidget's isActor rendering
const ACTOR_HEIGHT = 66; // taller than PARTICIPANT_HEIGHT so the stick figure itself can be legible — bottom-aligned to the same lifeline-start y, see layoutActor below
const NAMESPACE_GAP = 40;
const NAMESPACE_TOP_PADDING = 36; // room for the package title bar above the participant box
const NAMESPACE_BOTTOM_PADDING = 20; // breathing room below the participant box before the package's own border
const NAMESPACE_Y = 80;
const CANVAS_LEFT = 80;
const MESSAGE_ROW_HEIGHT = 50;
const MESSAGES_TOP_MARGIN = 40;
const LIFELINE_OVERLAP = 8;
const BOTTOM_PARTICIPANT_MARGIN = 40; // gap between the last message row and the bottom duplicate participant row
const ACTIVATION_WIDTH = 10;
const ACTIVATION_TOP_OFFSET = 16; // px below a message row's y where the activation bar starts (roughly where the arrow tip touches the lifeline)
const ACTIVATION_BOTTOM_OFFSET = 34; // px below the LAST relevant message row's y where the bar ends — extends a bit past it for a solid look
const MESSAGE_LABEL_LINE_HEIGHT = 14; // extra px per additional label line (multi-line ProducesResponseType labels, see CsProducesResponseScanner)

/**
 * Converts buildSequenceShape() output (boxes + call/return messages, see
 * CsSeqShapeFactory) into concrete UmlCanvas shape instances with computed
 * x/y/width/height, ready for addSequenceLayoutToCanvas below.
 *
 * Everything here is genuinely LINKED, not just visually coincidental (chat
 * 2026-07-19 rewrite): participant/lifeline/activation/bottom-twin/message
 * x/y positions are kept in sync by CsSeqDiaRelationHandler whenever a
 * participant moves (see UmlCanvasEngine.updateShapePosition) — this
 * function only computes the INITIAL, correct positions once at generation
 * time; nothing here is re-derived at render time anymore.
 * @param {{boxes: {namespace: string, participants: string[]}[], messages: {from: string, to: string, method: string, boundary: boolean, isReturn: boolean, stepRef: object}[]}} shape
 * @returns {{packages: object[], lifelines: object[], messages: object[], activations: object[], bottomParticipants: object[]}}
 */
export function buildCanvasSequenceLayout(shape) {
  const participantXById = new Map(); // participant name -> x-center (initial, at generation time)
  const participantIdByName = new Map(); // participant name -> shape id, links lifeline/activation/message/bottom-twin back to this TOP participant
  const packages = [];
  const actors = []; // standalone UmlSeqParticipantShape(isActor=true) instances — NOT nested in a package, see ACTOR_NAMESPACE handling below
  let cursorX = CANVAS_LEFT;

  shape.boxes.forEach(function layoutNamespace(box) {
    if (box.namespace === ACTOR_NAMESPACE) {
      // An actor represents a caller OUTSIDE the modeled system (see
      // CsSeqShapeFactory's "found message" doc) — no package/namespace
      // wrapper, just a bare stick-figure participant.
      box.participants.forEach(function layoutActor(name) {
        const participantId = uuid();
        // Bottom-aligned to where a regular participant's box would end
        // (NAMESPACE_Y + NAMESPACE_TOP_PADDING + PARTICIPANT_HEIGHT) so the
        // lifeline still starts at the same y despite the taller box.
        const actorY = NAMESPACE_Y + NAMESPACE_TOP_PADDING + PARTICIPANT_HEIGHT - ACTOR_HEIGHT;
        const actor = new UmlSeqParticipantShape(participantId, cursorX, actorY, ACTOR_WIDTH, ACTOR_HEIGHT, name, null, null, true);
        actors.push(actor);
        participantXById.set(name, cursorX + ACTOR_WIDTH / 2);
        participantIdByName.set(name, participantId);
        cursorX += ACTOR_WIDTH + PARTICIPANT_GAP;
      });
      cursorX += NAMESPACE_GAP - PARTICIPANT_GAP; // same overall gap before the next namespace box as between two real boxes
      return;
    }

    const participantBasedWidth = box.participants.length * PARTICIPANT_WIDTH + (box.participants.length - 1) * PARTICIPANT_GAP + NAMESPACE_TOP_PADDING;
    // Title bar renders "Package {name} - [{count}]" (see UmlPackageWidget.jsx)
    // on ONE line — a box sized only for its participants wraps/overflows for
    // long namespace names. Widen to fit the title too.
    const nameBasedWidth = box.namespace.length * 9 + 90;
    const namespaceWidth = Math.max(participantBasedWidth, nameBasedWidth);
    const pkg = new UmlPackageShape(uuid(), cursorX, NAMESPACE_Y, namespaceWidth, PARTICIPANT_HEIGHT + NAMESPACE_TOP_PADDING + NAMESPACE_BOTTOM_PADDING, box.namespace);

    let participantCursorX = cursorX + NAMESPACE_TOP_PADDING / 2;
    box.participants.forEach(function layoutParticipant(name) {
      const participantId = uuid();
      const participant = new UmlSeqParticipantShape(participantId, participantCursorX, NAMESPACE_Y + NAMESPACE_TOP_PADDING, PARTICIPANT_WIDTH, PARTICIPANT_HEIGHT, name, box.namespace);
      pkg.addChild(participant);
      participantXById.set(name, participantCursorX + PARTICIPANT_WIDTH / 2);
      participantIdByName.set(name, participantId);
      participantCursorX += PARTICIPANT_WIDTH + PARTICIPANT_GAP;
    });

    packages.push(pkg);
    cursorX += namespaceWidth + NAMESPACE_GAP;
  });

  const trueLifelineTopY = NAMESPACE_Y + PARTICIPANT_HEIGHT + NAMESPACE_TOP_PADDING + NAMESPACE_BOTTOM_PADDING;
  const lifelineTopY = trueLifelineTopY - LIFELINE_OVERLAP; // overlaps slightly INTO the participant box so it visually attaches instead of floating below the box-shadow
  // A multi-line label (e.g. several stacked ProducesResponseType entries)
  // needs extra vertical room reserved beyond the flat per-row height, or it
  // overlaps whatever comes right after it.
  const extraLabelHeight = shape.messages.reduce(function trackTallestLabel(max, m) {
    const lineCount = m.method ? m.method.split('\n').length : 1;
    return Math.max(max, (lineCount - 1) * MESSAGE_LABEL_LINE_HEIGHT);
  }, 0);
  const messagesBottomY = trueLifelineTopY + MESSAGES_TOP_MARGIN + shape.messages.length * MESSAGE_ROW_HEIGHT + extraLabelHeight;
  const bottomParticipantY = messagesBottomY + BOTTOM_PARTICIPANT_MARGIN;
  const lifelineBottomY = bottomParticipantY + LIFELINE_OVERLAP; // overlaps slightly INTO the bottom box, same reasoning as the top overlap

  const lifelines = Array.from(participantXById.entries()).map(function toLifeline([name, x]) {
    return new UmlSeqLifelineShape(uuid(), x, lifelineTopY, 2, lifelineBottomY - lifelineTopY, name, participantIdByName.get(name));
  });

  const bottomParticipants = Array.from(participantXById.entries()).map(function toBottomParticipant([name, x]) {
    const isActor = actors.some(function matchesActor(a) { return a.name === name; });
    const width = isActor ? ACTOR_WIDTH : PARTICIPANT_WIDTH;
    const height = isActor ? ACTOR_HEIGHT : PARTICIPANT_HEIGHT;
    return new UmlSeqParticipantShape(uuid(), x - width / 2, bottomParticipantY, width, height, name, null, participantIdByName.get(name), isActor);
  });

  const messages = shape.messages.map(function toMessageShape(message, index) {
    const y = trueLifelineTopY + MESSAGES_TOP_MARGIN + index * MESSAGE_ROW_HEIGHT;
    const fromX = participantXById.get(message.from) ?? CANVAS_LEFT;
    const toX = participantXById.get(message.to) ?? CANVAS_LEFT;
    const labelText = message.boundary ? `${message.method} (boundary)` : message.method;
    const lineCount = labelText ? labelText.split('\n').length : 1;
    const height = 24 + (lineCount - 1) * MESSAGE_LABEL_LINE_HEIGHT;
    // Default offset (0,0): label starts at the arrow's own top-left, same as
    // the old fixed rendering — fully draggable from there afterwards (own
    // UmlCaption, same pattern as a UmlConnector's label). Third-level calls
    // (`_field.Property.Method`) get a 40px head start away from the FROM
    // participant's activation bar, where they'd otherwise sit right on top
    // of it — normal single-hop calls keep the original (0,0).
    const label = new UmlCaption(uuid(), message.thirdLevel ? 40 : 0, 0, labelText);
    return new UmlSeqMessageShape(
      uuid(),
      Math.min(fromX, toX),
      y,
      Math.abs(toX - fromX) || 1,
      height,
      fromX,
      toX,
      label,
      message.boundary,
      participantIdByName.get(message.from),
      participantIdByName.get(message.to),
      message.isReturn,
      null,
      index,
      message.params,
    );
  });
  pairMessagesWithReturns(shape.messages, messages);

  const activations = computeActivationRanges(shape.messages).map(function toActivation(range) {
    const x = participantXById.get(range.participant) - ACTIVATION_WIDTH / 2;
    const y = trueLifelineTopY + MESSAGES_TOP_MARGIN + range.startIndex * MESSAGE_ROW_HEIGHT + ACTIVATION_TOP_OFFSET;
    const bottomY = trueLifelineTopY + MESSAGES_TOP_MARGIN + range.endIndex * MESSAGE_ROW_HEIGHT + ACTIVATION_BOTTOM_OFFSET;
    return new UmlSeqActivationShape(uuid(), x, y, ACTIVATION_WIDTH, Math.max(bottomY - y, ACTIVATION_WIDTH), participantIdByName.get(range.participant), range.participant, range.startIndex, range.endIndex);
  });

  return { packages, actors, lifelines, messages, activations, bottomParticipants };
}

/**
 * Cross-references each call UmlSeqMessageShape with its matching return
 * shape (same stepRef, see CsSeqShapeFactory.buildEventRows) via
 * pairedMessageId on BOTH — lets a vertical drag be clamped so a call never
 * sinks below its own return and a return never rises above its own call
 * (see CsSeqDiaRelationHandler.clampSeqMessageY).
 * @param {{isReturn: boolean, stepRef: object}[]} sourceMessages
 * @param {object[]} messageShapes - same order/length as sourceMessages
 */
function pairMessagesWithReturns(sourceMessages, messageShapes) {
  const callShapeByStep = new Map();
  sourceMessages.forEach(function findCall(m, i) {
    if (!m.isReturn) {
      callShapeByStep.set(m.stepRef, messageShapes[i]);
    }
  });
  sourceMessages.forEach(function linkReturn(m, i) {
    if (m.isReturn) {
      const callShape = callShapeByStep.get(m.stepRef);
      if (callShape) {
        callShape.pairedMessageId = messageShapes[i].id;
        messageShapes[i].pairedMessageId = callShape.id;
      }
    }
  });
}

/**
 * Pairs each 'return' message back to its matching 'call' message by object
 * identity (CsSeqShapeFactory.buildEventRows stamps the SAME step object
 * reference onto both halves of a call/return pair) to get an EXACT
 * activation span per call \u2014 replaces the old "first time in, last time
 * seen" heuristic (2026-07-19) with the real nesting boundaries. A call that
 * never gets a return (boundary steps, or the outermost entry point which
 * has no explicit "outer caller" of its own) contributes no activation bar.
 * A participant re-entered multiple times gets one SEPARATE bar per call.
 * @param {{isReturn: boolean, stepRef: object, from: string, to: string}[]} messages
 * @returns {{participant: string, startIndex: number, endIndex: number}[]}
 */
function computeActivationRanges(messages) {
  const ranges = [];
  const openCallIndexByStep = new Map();

  messages.forEach(function scan(m, i) {
    if (!m.isReturn) {
      openCallIndexByStep.set(m.stepRef, i);
      return;
    }
    const callIndex = openCallIndexByStep.get(m.stepRef);
    if (callIndex !== undefined) {
      ranges.push({ participant: m.from, startIndex: callIndex, endIndex: i }); // return's `from` IS the participant that was active (see buildEventRows: return swaps from/to back to callee->caller)
    }
  });

  return ranges;
}

/**
 * Pushes a buildCanvasSequenceLayout() result into umlCanvasEngine, in
 * back-to-front z-order: lifelines first (bottom), then activation bars,
 * then messages (so arrowheads render ON TOP of the bars, not hidden behind
 * them), then bottom duplicate participants, then namespace packages + their
 * participant children (top) — so lines/arrows/bars visually pass behind
 * the participant boxes rather than over them.
 * @param {{packages: object[], lifelines: object[], messages: object[], activations: object[], bottomParticipants: object[]}} layout
 */
export function addSequenceLayoutToCanvas(layout) {
  layout.lifelines.forEach(function addLifeline(shape) {
    umlCanvasEngine.addShape(shape);
  });
  layout.activations.forEach(function addActivation(shape) {
    umlCanvasEngine.addShape(shape);
  });
  layout.messages.forEach(function addMessage(shape) {
    umlCanvasEngine.addShape(shape);
  });
  layout.bottomParticipants.forEach(function addBottomParticipant(shape) {
    umlCanvasEngine.addShape(shape);
  });
  layout.actors.forEach(function addActor(shape) {
    umlCanvasEngine.addShape(shape);
  });
  layout.packages.forEach(function addPackage(pkg) {
    umlCanvasEngine.addShape(pkg);
    // addChild() (called during buildCanvasSequenceLayout) stamped each child's
    // zIndex as pkg.zIndex+1 BEFORE addShape's bringToFront gave pkg its real,
    // final zIndex — restamp now so participants render above the
    // already-added lifelines/messages/activations instead of at zIndex 1.
    pkg.children.forEach(function restampChildZIndex(child) {
      child.zIndex = pkg.zIndex + 1;
    });
  });
}
